// src/pages/TeamPage.tsx

import { Layout, Typography, Row, Col, Card, Avatar, Tag, Space } from 'antd';
import {
  UserOutlined,
  GithubOutlined,
  LinkedinOutlined,
  MailOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  StarOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import '../styles/teamPage.css';
import LibertyPicture from '../assets/team/liberty.jpg';
import SamuelPicture from '../assets/team/samuel.jpg';
import VincePicture from '../assets/team/vince.jpg';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { buildCanonicalUrl, buildOgImageUrl } from '../utils/seo';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

interface SocialLinks {
  github?: string;
  linkedin?: string;
  website?: string;
  email?: string;
}

interface TeamMember {
  name: string;
  title: string;
  avatar: string;
  description: string;
  skills: string[];
  social: SocialLinks;
  gradient: string;
}

const TeamPage = () => {
  const { t } = useTranslation();
  const canonicalUrl = buildCanonicalUrl('/team');

  const teamMembers: TeamMember[] = [
    {
      name: t('team.members.liberty.name'),
      title: t('team.members.liberty.title'),
      avatar: LibertyPicture,
      description: t('team.members.liberty.description'),
      // Fix: Explicitly cast the skills array
      skills: (t('team.members.liberty.skills', { returnObjects: true }) as string[]) || [],
      social: {
        linkedin: 'https://www.linkedin.com/in/%E5%B3%BB%E7%9B%8A-%E5%8A%89-480948346/',
        email: 'liberty.liu@ailicorn.com'
      },
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      name: t('team.members.samuel.name'),
      title: t('team.members.samuel.title'),
      avatar: SamuelPicture,
      description: t('team.members.samuel.description'),
      skills: (t('team.members.samuel.skills', { returnObjects: true }) as string[]) || [],
      social: {
        website: 'https://samuel-website.herokuapp.com/',
        linkedin: 'https://www.linkedin.com/in/samuelysliu/',
        email: 'samuel.liu@ailicorn.com'
      },
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      name: t('team.members.vince.name'),
      title: t('team.members.vince.title'),
      avatar: VincePicture,
      description: t('team.members.vince.description'),
      skills: (t('team.members.vince.skills', { returnObjects: true }) as string[]) || [],
      social: {
        github: 'https://github.com',
        linkedin: 'https://linkedin.com',
        email: 'vince.tsai@ailicorn.com'
      },
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    /*
    {
      name: t('team.members.person4.name'),
      title: t('team.members.person4.title'),
      avatar: '/team/member4.jpg',
      description: t('team.members.person4.description'),
      skills: (t('team.members.person4.skills', { returnObjects: true }) as string[])|| [],
      social: {
        github: 'https://github.com',
        linkedin: 'https://linkedin.com',
        email: 'liberty.liu@ailicorn.com'
      },
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      name: t('team.members.person5.name'),
      title: t('team.members.person5.title'),
      avatar: '/team/member5.jpg',
      description: t('team.members.person5.description'),
      skills: (t('team.members.person5.skills', { returnObjects: true }) as string[])|| [],
      social: {
        github: 'https://github.com',
        linkedin: 'https://linkedin.com',
        email: 'liberty.liu@ailicorn.com'
      },
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    */
  ];

  const peopleSchema = teamMembers.map((member) => ({
    '@type': 'Person',
    name: member.name,
    jobTitle: member.title,
    description: member.description,
    email: member.social.email,
    worksFor: {
      '@type': 'Organization',
      name: 'AIlicorn',
    },
    image: {
      '@type': 'ImageObject',
      url: buildOgImageUrl(member.avatar),
      name: `${member.name} - ${member.title}`,
    },
    knowsAbout: member.skills,
    sameAs: (
      [
        member.social.github,
        member.social.linkedin,
        member.social.website,
      ].filter(Boolean) as string[]
    ),
  }));

  const teamSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: t('seo.team.title'),
      description: t('seo.team.description'),
      url: canonicalUrl,
      inLanguage: ['en', 'zh-TW'],
      about: {
        '@type': 'Organization',
        name: 'AIlicorn',
        description: t('seo.home.description'),
      },
      mainEntity: {
        '@type': 'ItemList',
        name: 'AIlicorn Team',
        itemListElement: peopleSchema.map((person, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: person,
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@graph': peopleSchema,
    },
  ];

  return (
    <Layout className="team-page-layout">
      <SEO
        title={t('seo.team.title')}
        description={t('seo.team.description')}
        keywords={t('seo.team.keywords')}
        ogImage={buildOgImageUrl('/logo.png')}
        ogUrl={canonicalUrl}
        canonical={canonicalUrl}
        jsonLd={teamSchema}
      />
      <Content className="team-page">
        {/* Hero Section */}
        <div className="team-hero">
          <div className="team-hero-content">
            <RocketOutlined className="team-hero-icon" />
            <Title level={1} className="team-hero-title">
              {t('team.hero.title')}
            </Title>
            <Paragraph className="team-hero-subtitle">
              {t('team.hero.subtitle')}
            </Paragraph>
            <div className="team-stats">
              <div className="stat-item">
                <ThunderboltOutlined />
                <Text strong>2+</Text>
                <Text type="secondary">{t('team.hero.stats.clients')}</Text>
              </div>
              <div className="stat-item">
                <StarOutlined />
                <Text strong>15+</Text>
                <Text type="secondary">{t('team.hero.stats.awards')}</Text>
              </div>
              <div className="stat-item">
                <UserOutlined />
                <Text strong>10+</Text>
                <Text type="secondary">{t('team.hero.stats.members')}</Text>
              </div>
            </div>
          </div>
        </div>

        {/* Team Members Grid */}
        <div className="section-container">
          <Row gutter={[32, 32]} style={{ marginTop: 60 }}>
            {teamMembers.map((member, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card
                  className="tech-team-card"
                  hoverable
                  bordered={false}
                >
                  {/* Gradient Header */}
                  <div
                    className="card-gradient-header"
                    style={{ background: member.gradient }}
                  >
                    <div className="card-pattern"></div>
                  </div>

                  {/* Avatar Section */}
                  <div className="card-avatar-section">
                    <Avatar
                      size={120}
                      src={member.avatar}
                      alt={`${member.name} - ${member.title}`}
                      icon={<UserOutlined />}
                      className="card-avatar"
                    />
                  </div>

                  {/* Content Section */}
                  <div className="card-content-section">
                    <Title level={4} className="member-name">
                      {member.name}
                    </Title>
                    <Text className="member-position">
                      {member.title}
                    </Text>

                    <Paragraph className="member-bio">
                      {member.description}
                    </Paragraph>

                    {/* Skills Tags */}
                    <div className="member-skills">
                      {member.skills.map((skill: string, idx: number) => (
                        <Tag
                          key={idx}
                          className="skill-tag"
                          color="blue"
                        >
                          {skill}
                        </Tag>
                      ))}
                    </div>

                    {/* Social Links */}
                    <Space className="member-social" size="middle">
                      {member.social.github && (
                        <a href={member.social.github} className="social-icon">
                          <GithubOutlined />
                        </a>
                      )}
                      {member.social.linkedin && (
                        <a href={member.social.linkedin} className="social-icon">
                          <LinkedinOutlined />
                        </a>
                      )}
                      {member.social.website && (
                        <a href={member.social.website} className="social-icon">
                          <GlobalOutlined />
                        </a>
                      )}
                      {member.social.email && (
                        <a href={`mailto:${member.social.email}`} className="social-icon">
                          <MailOutlined />
                        </a>
                      )}
                    </Space>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </Content>
      <Footer />
    </Layout>
  );
};

export default TeamPage;
